import React from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import FormControlLabel from "../wrappers/FormControlLabel";
import FormLabel from "../wrappers/FormLabel";
import Radio from "../wrappers/Radio";
import RadioGroup from "../wrappers/RadioGroup";
import Typography from "../wrappers/Typography";
import { getOptionControlValue, isSameValue } from "./SearchFilters.helpers";

/**
 * Renderiza uma seleção única controlada por opções estáveis.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável do filtro.
 * @param {string} pProps.label Rótulo acessível do grupo.
 * @param {*} pProps.value Valor controlado pelo consumidor.
 * @param {Array<object>} pProps.options Opções do controle.
 * @param {Function} pProps.onChange Recebe o valor escolhido sem coerção.
 * @param {boolean} [pProps.disabled=false] Desabilita o controle.
 * @returns {React.ReactElement}
 */
const SearchFilterSingleSelect = (pProps) => {
  const { id, label, value, options, onChange, disabled = false } = pProps;
  const xSelectedOption = options.find((pOption) => isSameValue(pOption.value, value));
  const xLabelId = `${id}-label`;

  /** Resolve a opção pelo ID técnico e emite apenas mudanças reais. */
  const pvHandleChange = (pEvent) => {
    const xOption = options.find(
      (pOption) => getOptionControlValue(pOption.id) === pEvent.target.value && !pOption.disabled,
    );

    if (!xOption || isSameValue(xOption.value, value)) {
      return;
    }

    onChange(xOption.value);
  };

  return (
    <Box>
      <FormLabel disabled={disabled} id={xLabelId} sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={500}>
          {label}
        </Typography>
      </FormLabel>
      <RadioGroup
        aria-label={label}
        aria-labelledby={xLabelId}
        name={`${id}-options`}
        onChange={pvHandleChange}
        value={xSelectedOption ? getOptionControlValue(xSelectedOption.id) : ""}
      >
        {options.map((pOption) => (
          <FormControlLabel
            key={pOption.id}
            control={<Radio size="small" />}
            disabled={disabled || pOption.disabled}
            label={<Typography variant="caption">{pOption.label}</Typography>}
            value={getOptionControlValue(pOption.id)}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

SearchFilterSingleSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      disabled: PropTypes.bool,
      neutral: PropTypes.bool,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default SearchFilterSingleSelect;
